import { FC, useEffect, useMemo } from 'react';
import {
  Outlet,
  useMatches,
  useSearchParams,
  useNavigate,
  useParams,
  useLocation,
  ScrollRestoration
} from 'react-router';
import { CurrencyCurrency } from '@linktivity/ars-neo';
import { LOCALE_OPTIONS } from '@link/share/constants';
import { setLocale, DEFAULT_LOCALE, getLocaleValues } from '@link/share/i18n';
import Footer from '../Footer';
import Header from '../Header';
import { CartStore, LocaleOption, SearchConfig } from '../Header/header.types';
import styles from './base.module.css';

interface BaseProps {
  localeList?: readonly LocaleOption[];
  defaultLocale?: string;
  supportedCurrencies?: CurrencyCurrency[];
  supportCart?: boolean;
  cartStore?: CartStore;
  currentCurrency?: CurrencyCurrency;
  onCurrencyChange?: (currency: CurrencyCurrency) => void;
  appTitle?: string;
  appLogo?: string;
  enableSearch?: boolean;
  showCurrency?: boolean;
  searchPlaceholder?: string;
  isPasswordAuth?: boolean;
  logout: () => Promise<void>;
  loggedIn: boolean;
  onSearch?: (query: string) => void;
}

const Base: FC<BaseProps> = ({
  appTitle,
  appLogo,
  localeList = LOCALE_OPTIONS,
  showCurrency = false,
  supportedCurrencies,
  supportCart = false,
  cartStore,
  currentCurrency,
  onCurrencyChange,
  defaultLocale = DEFAULT_LOCALE,
  enableSearch = false,
  searchPlaceholder,
  isPasswordAuth,
  logout,
  loggedIn,
  onSearch
}) => {
  const matches = useMatches();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useParams();
  const location = useLocation();

  const supportedLocales = useMemo(
    () => getLocaleValues(localeList),
    [localeList]
  );
  const langParam = useMemo(() => searchParams.get('lang'), [searchParams]);
  const isValidLang = useMemo(
    () => lang && supportedLocales.includes(lang),
    [lang, supportedLocales]
  );

  useEffect(() => {
    if (isValidLang && lang) {
      setLocale(lang);
    } else {
      const targetLang =
        langParam && supportedLocales.includes(langParam)
          ? langParam
          : defaultLocale;
      setLocale(targetLang);
      const pathWithoutLang =
        lang && lang !== targetLang
          ? location.pathname.substring(lang.length + 1)
          : location.pathname;
      navigate(`/${targetLang}${pathWithoutLang}${location.search}`, {
        replace: true,
        state: location.state
      });
    }
  }, [
    isValidLang,
    lang,
    navigate,
    langParam,
    supportedLocales,
    defaultLocale,
    location.pathname,
    location.search,
    location.state
  ]);

  // set page title
  useEffect(() => {
    const currentMatch = matches[matches.length - 1];
    const handle = currentMatch?.handle as { title?: string } | undefined;
    if (handle?.title) {
      document.title = handle.title;
    } else if (appTitle) {
      document.title = appTitle;
    }
  }, [matches, appTitle]);

  const searchConfig: SearchConfig | undefined = enableSearch
    ? {
        enabled: true,
        placeholder: searchPlaceholder,
        onSearch: onSearch || (() => {})
      }
    : undefined;

  // Don't render children until lang is valid to prevent index route from executing
  if (!isValidLang) {
    return null;
  }

  return (
    <div className={styles.base}>
      <ScrollRestoration />
      <Header
        logo={appLogo}
        localeList={localeList}
        showCurrency={showCurrency}
        supportedCurrencies={supportedCurrencies}
        supportCart={supportCart}
        cartStore={cartStore}
        currentCurrency={currentCurrency}
        onCurrencyChange={onCurrencyChange}
        isPasswordAuth={isPasswordAuth}
        searchConfig={searchConfig}
        logout={logout}
        loggedIn={loggedIn}
      />
      <div className={styles.container}>
        <Outlet />
      </div>
      <Footer logo={appLogo} />
    </div>
  );
};

export default Base;
